// Website translations (react-i18next). French is Quebec French (fr-CA); see
// src/locales/GLOSSARY-fr-CA.md for the words to use and src/locales/README.md for how
// to add text.
//
// Language: the person's saved choice (FR | EN switch: localStorage "bolt.language", and
// a cookie of the same name that outlives signing out), then the browser (fr-* gives
// French), then English. On every change the page's <html lang>, the API's
// Accept-Language header and the date libraries follow.
import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import axios from "axios";
import dayjs from "dayjs";
import moment from "moment";
import "dayjs/locale/fr-ca";
import "moment/locale/fr-ca";

import en from "../locales/en";
import fr from "../locales/fr";
import {
  DEFAULT_LANGUAGE,
  STORAGE_KEY,
  SUPPORTED,
  localeOf,
  normalizeLanguage,
} from "./languages";

export const NAMESPACES = Object.keys(en);

const isTest = process.env.NODE_ENV === "test";

// What changes outside React when the language does.
export const applyLanguage = (lng) => {
  const lang = normalizeLanguage(lng) || DEFAULT_LANGUAGE;
  const locale = localeOf(lang);
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("lang", locale);
  }
  // Read by the backend once it localizes messages, emails and PDFs. A CORS-safelisted
  // header, and the API allows every header anyway (config/cors.php).
  if (axios && axios.defaults && axios.defaults.headers) {
    axios.defaults.headers.common = axios.defaults.headers.common || {};
    axios.defaults.headers.common["Accept-Language"] = locale;
  }
  if (dayjs && typeof dayjs.locale === "function")
    dayjs.locale(lang === "fr" ? "fr-ca" : "en");
  if (moment && typeof moment.locale === "function")
    moment.locale(lang === "fr" ? "fr-ca" : "en");
};

// A configured, initialized instance. The app uses the default one below; tests can build
// their own with { lng } or with the detector reading a mocked localStorage/navigator.
export const createI18n = ({ lng, detect = true, react = true } = {}) => {
  const instance = i18next.createInstance();
  if (detect && !lng) instance.use(LanguageDetector);
  if (react) instance.use(initReactI18next);
  instance.init({
    resources: { en, fr },
    lng,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: SUPPORTED,
    nonExplicitSupportedLngs: true, // fr-CA -> fr
    load: "languageOnly",
    ns: NAMESPACES,
    defaultNS: "common",
    fallbackNS: "common",
    interpolation: { escapeValue: false }, // React escapes already
    returnNull: false,
    initImmediate: false, // resources are bundled: ready before the first render
    react: { useSuspense: false },
    detection: {
      order: ["localStorage", "cookie", "navigator"],
      lookupLocalStorage: STORAGE_KEY,
      lookupCookie: STORAGE_KEY, // survives the localStorage.clear() of signing out
      // Only an explicit FR | EN choice is saved (LanguageSwitch), not what the browser said.
      caches: [],
      convertDetectedLanguage: (l) => normalizeLanguage(l) || l,
    },
  });
  return instance;
};

// Tests run in English unless a test asks otherwise, so existing assertions keep working.
const i18n = createI18n(isTest ? { lng: "en", detect: false } : {});
i18n.on("languageChanged", applyLanguage);
applyLanguage(i18n.resolvedLanguage || i18n.language);

export const currentLanguage = () =>
  normalizeLanguage(i18n.resolvedLanguage || i18n.language) || DEFAULT_LANGUAGE;
export const currentLocale = () => localeOf(currentLanguage());

export default i18n;
